import { useShallow } from 'zustand/react/shallow';

import { validateFile } from '@/lib/utils';

import { DocumentUploadHandlerFactory } from '../handlers/document-upload.handler';

import { selectFormValidationState } from '@/store/document-upload.selectors';
import { useDocumentUploadStore } from '@/store/document-upload.store';

type UseDocumentUploadOptions = {
  onSuccess: (file: File) => Promise<void>;
};

export function useDocumentUpload({ onSuccess }: UseDocumentUploadOptions) {
  const { documentType, uploadedFile, webPageUrl, textName, textContent } = useDocumentUploadStore(
    useShallow(selectFormValidationState)
  );

  const setUploadedFile = useDocumentUploadStore(state => state.setUploadedFile);
  const setValidationErrors = useDocumentUploadStore(state => state.setValidationErrors);
  const setWebPageError = useDocumentUploadStore(state => state.setWebPageError);
  const setTextError = useDocumentUploadStore(state => state.setTextError);
  const clearErrors = useDocumentUploadStore(state => state.clearErrors);
  const reset = useDocumentUploadStore(state => state.reset);

  const isSubmitDisabled =
    (documentType === 'file' && !uploadedFile) ||
    (documentType === 'url' && !webPageUrl.trim()) ||
    (documentType === 'text' && (!textName.trim() || !textContent.trim()));

  const handleFileSelect = (files: File[]) => {
    if (files.length === 0) {
      setValidationErrors([]);
      setUploadedFile(null);
      return;
    }

    const file = files[0];
    const error = validateFile(file);

    if (error) {
      setValidationErrors([{ file: file.name, message: error }]);
      setUploadedFile(null);
    } else {
      setValidationErrors([]);
      setUploadedFile(file);
    }
  };

  const handleSubmit = async (): Promise<boolean> => {
    clearErrors();

    try {
      const handler = DocumentUploadHandlerFactory.create(documentType, {
        file: uploadedFile,
        url: webPageUrl,
        textName,
        textContent,
      });

      const validationResult = handler.validate();

      if (!validationResult.isValid) {
        if (documentType === 'file') {
          setValidationErrors([{ file: 'general', message: validationResult.error || '' }]);
        } else if (documentType === 'url') {
          setWebPageError(validationResult.error || null);
        } else if (documentType === 'text') {
          setTextError(validationResult.error || null);
        }
        return false;
      }

      const file = handler.convertToFile();
      if (!file) return false;

      await onSuccess(file);
      return true;
    } catch {
      setValidationErrors([{ file: 'general', message: 'Upload failed. Please try again.' }]);
      return false;
    }
  };

  return {
    documentType,
    isSubmitDisabled,
    handleFileSelect,
    handleSubmit,
    reset,
  };
}
